import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

// Define the Holiday interface
export interface Holiday {
  id: number;
  date: string;
  holidayCalID: string;
  freeday: string;
  holiday: string;
  holidayId: string;
  txtShort: string;
  txtLong: string;
  created_at: string;
  modified_at: string;
  deleted_at: null | string;
  sap_created_at: null | string;
  sap_modified_at: null | string;
}

// Calendar Event interface for FullCalendar
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  backgroundColor: string;
  textColor: string;
  category: "gazetted" | "restricted" | "general";
  description?: string;
  priority: "high" | "medium" | "low";
  type: "holiday";
  department?: string;
}

// State interface
export interface CalendarState {
  holidays: Holiday[];
  calendarEvents: CalendarEvent[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filterTab: "all" | "restricted" | "gazetted";
  selectedMonth: number;
  selectedYear: number;
}

// API Response interface
interface HolidayApiResponse {
  status: number;
  message: string;
  data: {
    data: Holiday[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Request parameters interface
interface FetchHolidaysParams {
  page?: number;
  limit?: number;
  query?: string;
  holidayCalID?: string;
}

// Initial state
const initialState: CalendarState = {
  holidays: [],
  calendarEvents: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 500,
    total: 0,
    totalPages: 0,
  },
  filterTab: "all",
  selectedMonth: new Date().getMonth(),
  selectedYear: new Date().getFullYear(),
};

// Helper function to format holidays for calendar
const formatHolidaysForCalendar = (holidays: Holiday[]): CalendarEvent[] => {
  return holidays.map((holiday) => {
    // Determine holiday type based on txtLong field
    const isGazetted =
      holiday.txtLong?.includes("-GH") || holiday.txtLong?.includes("GAZETTED");
    const isRestricted =
      holiday.txtLong?.includes("-RH") ||
      holiday.txtLong?.includes("RESTRICTED");

    let backgroundColor: string;
    let textColor: string;
    let category: "gazetted" | "restricted" | "general";

    if (isGazetted) {
      backgroundColor = "#FAE7E7";
      textColor = "#E70D0D";
      category = "gazetted";
    } else if (isRestricted) {
      backgroundColor = "#E7F7E7";
      textColor = "#28A745";
      category = "restricted";
    } else {
      backgroundColor = "#E3F2FD";
      textColor = "#1976D2";
      category = "general";
    }

    return {
      id: holiday.id.toString(),
      title: holiday.txtShort || holiday.txtLong || "Holiday",
      start: holiday.date,
      end: holiday.date,
      backgroundColor,
      textColor,
      category,
      description: holiday.txtLong || holiday.txtShort,
      priority: isGazetted ? "high" : "medium",
      type: "holiday",
      department: "HR",
    };
  });
};

// Async thunk to fetch holidays
export const fetchHolidays = createAsyncThunk<
  HolidayApiResponse,
  FetchHolidaysParams,
  { rejectValue: string }
>("calendar/fetchHolidays", async (params, { rejectWithValue }) => {
  try {
    const { page = 1, limit = 500, query = "", holidayCalID } = params;

    // CRITICAL: holidayCalID is now MANDATORY
    if (!holidayCalID) {
      return rejectWithValue("holidayCalID is required to fetch holidays");
    }

    const requestPayload = {
      page,
      limit,
      query,
      holidayCalID, // Always include holidayCalID (now mandatory)
    };

    console.log("Fetching holidays with payload:", requestPayload);

    const response = await Api.post(API_URLS.HOLIDAY.LIST, requestPayload);

    if (response.data.status === 200) {
      return response.data;
    } else {
      return rejectWithValue(
        response.data.message || "Failed to fetch holidays"
      );
    }
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch holidays");
  }
});

// Calendar slice
const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setFilterTab: (
      state,
      action: PayloadAction<"all" | "restricted" | "gazetted">
    ) => {
      state.filterTab = action.payload;
    },
    setSelectedMonth: (state, action: PayloadAction<number>) => {
      state.selectedMonth = action.payload;
    },
    setSelectedYear: (state, action: PayloadAction<number>) => {
      state.selectedYear = action.payload;
    },
    setSelectedDate: (
      state,
      action: PayloadAction<{ month: number; year: number }>
    ) => {
      state.selectedMonth = action.payload.month;
      state.selectedYear = action.payload.year;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCalendarState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch holidays
      .addCase(fetchHolidays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.loading = false;
        state.holidays = action.payload.data.data;
        state.calendarEvents = formatHolidaysForCalendar(
          action.payload.data.data
        );
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: action.payload.data.totalPages,
        };
        state.error = null;
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch holidays";
      });
  },
});

// Export actions
export const {
  setFilterTab,
  setSelectedMonth,
  setSelectedYear,
  setSelectedDate,
  clearError,
  resetCalendarState,
} = calendarSlice.actions;

// Selectors
export const selectAllHolidays = (state: { calendarState: CalendarState }) =>
  state.calendarState.holidays;

export const selectCalendarEvents = (state: { calendarState: CalendarState }) =>
  state.calendarState.calendarEvents;

export const selectFilteredEvents = (state: {
  calendarState: CalendarState;
}) => {
  const { calendarEvents, filterTab } = state.calendarState;

  if (filterTab === "all") {
    return calendarEvents;
  }

  return calendarEvents.filter((event) => event.category === filterTab);
};

export const selectCalendarLoading = (state: {
  calendarState: CalendarState;
}) => state.calendarState.loading;

export const selectCalendarError = (state: { calendarState: CalendarState }) =>
  state.calendarState.error;

export const selectCalendarPagination = (state: {
  calendarState: CalendarState;
}) => state.calendarState.pagination;

export const selectFilterTab = (state: { calendarState: CalendarState }) =>
  state.calendarState.filterTab;

export const selectSelectedMonth = (state: { calendarState: CalendarState }) =>
  state.calendarState.selectedMonth;

export const selectSelectedYear = (state: { calendarState: CalendarState }) =>
  state.calendarState.selectedYear;

export const selectHolidayStats = (state: { calendarState: CalendarState }) => {
  const { calendarEvents } = state.calendarState;

  return {
    total: calendarEvents.length,
    gazetted: calendarEvents.filter((h) => h.category === "gazetted").length,
    restricted: calendarEvents.filter((h) => h.category === "restricted")
      .length,
    general: calendarEvents.filter((h) => h.category === "general").length,
  };
};

// Selector to get holidayCalID from employee profile
export const selectEmployeeHolidayCalID = (state: any) => {
  // The employee profile response structure is: response.data.data
  // Where data contains the employee object with employee_work_schedules
  const employeeData = state.employeesState?.data?.data;
  const workSchedule = employeeData?.employee_work_schedules;

  return workSchedule?.holidayCalID || null;
};

// Export reducer
export default calendarSlice.reducer;
